import React from 'react';
import Link from 'next/link';
import { HomepageShowroomItem, defaultHomepageConfig } from '@/lib/homepage-config';

function extractTimeLabel(value: string) {
  const text = String(value || '').trim();
  if (!text) return '08:30 - 21:00';
  const match = text.match(/\(([^)]+)\)/);
  return match?.[1] || '08:30 - 21:00';
}

function extractPhoneLabel(value: string) {
  const text = String(value || '').trim();
  if (!text) return '1900 6858';
  const match = text.match(/(\+?\d[\d\s.-]{6,})/);
  return match?.[1]?.trim() || text;
}

function buildMapHref(value: string) {
  const href = String(value || '').trim();
  return href || '#';
}

export default function ShowroomSection({
  title = defaultHomepageConfig.showroomSection.title,
  items = defaultHomepageConfig.showroomSection.items,
}: {
  title?: string;
  items?: HomepageShowroomItem[];
}) {
  const showrooms = items
    .filter((item) => item.enabled)
    .sort((a, b) => a.sort_order - b.sort_order);
  const safeShowrooms = showrooms.length ? showrooms : defaultHomepageConfig.showroomSection.items;
  const displayShowrooms = safeShowrooms.slice(0, 2);

  return (
    <section className="py-12">
      <div className="max-w-[1500px] mx-auto px-4">
        <div className="rounded-none border border-slate-200 bg-white px-4 py-8 shadow-sm sm:px-5">
          {/* Header */}
          <div className="mb-8">
            <p className="text-[11px] font-bold tracking-[0.2em] text-[#eab308] uppercase mb-1">
              SHOWROOM
            </p>
            <div className="flex items-center">
              <div className="w-1.5 h-7 bg-amber-500 mr-3 rounded-sm"></div>
              <h2 className="text-2xl font-bold text-[#0f172a]">
                {title || 'Hệ Thống Cửa Hàng DN TECH'}
              </h2>
            </div>
            <p className="mt-3 max-w-2xl text-sm text-slate-600">
              Trải nghiệm sản phẩm thực tế, nhận tư vấn build PC và hỗ trợ bảo hành trực tiếp.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Showroom Cards (2 cols) */}
            {displayShowrooms.map((showroom, index) => {
              const isMain = index === 0;
              return (
                <div
                  key={index}
                  className={`bg-white rounded-none overflow-hidden border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col h-full ${
                    displayShowrooms.length === 1 && index === 0 ? 'lg:col-span-2' : ''
                  }`}
                >
                  {showroom.image ? (
                    <div className="h-96 overflow-hidden bg-slate-100 sm:h-[28.8rem] lg:h-[33.6rem]">
                      <img
                        src={showroom.image}
                        alt={showroom.name}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="h-[21.6rem] bg-[#0f172a] p-5 text-white sm:h-96 lg:h-[28.8rem]">
                      <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-200">
                        DN TECH
                      </div>
                      <div className="mt-3 text-2xl font-black">Showroom trải nghiệm</div>
                    </div>
                  )}
                  {/* Card Header */}
                  <div className="bg-[#0f172a] px-5 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${isMain ? 'bg-[#eab308]' : 'bg-gray-700'}`}
                      >
                        {isMain ? '★' : showroom.number}
                      </div>
                      <div>
                        <h3 className="text-white font-bold text-[15px] leading-tight">
                          {showroom.name}
                        </h3>
                        {isMain && (
                          <p className="text-[#eab308] text-[10px] font-bold tracking-wider uppercase mt-0.5">
                            SHOWROOM CHÍNH
                          </p>
                        )}
                      </div>
                    </div>
                    <Link
                      href={buildMapHref(showroom.mapHref)}
                      target="_blank"
                      className="border border-white/20 text-white/80 hover:text-white hover:border-white/50 text-[11px] px-3 py-1.5 rounded transition-colors"
                    >
                      Xem bản đồ
                    </Link>
                  </div>

                  {/* Card Body */}
                  <div className="p-5 flex flex-col flex-grow">
                    <div className="flex items-start gap-2.5 mb-5">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                        className="text-[#eab308] mt-0.5 shrink-0"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <p className="text-[13px] text-gray-600 leading-relaxed">
                        {showroom.address}
                      </p>
                    </div>

                    <div className="grid grid-cols-3 gap-2 mb-5">
                      <div className="bg-gray-50/80 rounded-none p-2 text-center flex flex-col items-center justify-center border border-gray-100">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                          className="text-[#eab308] mb-1"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                        <span className="text-[9px] text-gray-400 font-bold uppercase mb-0.5">
                          HOTLINE
                        </span>
                        <span className="text-[11px] font-bold text-gray-800">
                          {extractPhoneLabel(showroom.phone)}
                        </span>
                      </div>
                      <div className="bg-gray-50/80 rounded-none p-2 text-center flex flex-col items-center justify-center border border-gray-100">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                          className="text-[#eab308] mb-1"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span className="text-[9px] text-gray-400 font-bold uppercase mb-0.5">
                          GIỜ MỞ CỬA
                        </span>
                        <span className="text-[11px] font-bold text-gray-800">
                          {extractTimeLabel(showroom.phone)}
                        </span>
                      </div>
                      <div className="bg-gray-50/80 rounded-none p-2 text-center flex flex-col items-center justify-center border border-gray-100">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                          className="text-[#eab308] mb-1"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                          />
                        </svg>
                        <span className="text-[9px] text-gray-400 font-bold uppercase mb-0.5">
                          BẢO HÀNH
                        </span>
                        <span className="text-[10px] font-bold text-gray-800 text-center leading-tight">
                          {showroom.warranty || 'T2 - T7 | 8:30 - 17:30'}
                        </span>
                      </div>
                    </div>

                    <div className="mt-auto">
                      <Link
                        href={buildMapHref(showroom.mapHref)}
                        target="_blank"
                        className="flex items-center justify-center w-full py-2.5 rounded-none border border-gray-200 text-[13px] font-bold text-gray-700 hover:bg-gray-50 hover:text-[#eab308] hover:border-[#eab308] transition-colors gap-2"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                          />
                        </svg>
                        Chỉ đường nhanh
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Info Column (Col 3) */}
            <div className="flex flex-col gap-6">
              {/* Thông tin nhanh */}
              <div className="bg-[#0f172a] rounded-none p-6 text-white relative overflow-hidden flex-shrink-0">
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#eab308] opacity-10 rounded-full blur-3xl"></div>
                <h3 className="text-[#eab308] text-[11px] font-bold tracking-widest uppercase mb-4 relative z-10">
                  THÔNG TIN NHANH
                </h3>

                <div className="flex items-end gap-3 mb-6 relative z-10">
                  <span className="text-5xl font-black text-[#eab308] leading-none">
                    {safeShowrooms.length}
                  </span>
                  <span className="text-[13px] text-gray-400 leading-tight pb-1">
                    Showroom
                    <br />
                    đang hoạt động
                  </span>
                </div>

                <div className="space-y-5 relative z-10">
                  <div className="flex items-start gap-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                      className="text-[#eab308] mt-0.5 shrink-0"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                    <div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">
                        HỖ TRỢ
                      </div>
                      <div className="text-[13px] font-medium text-white/90">
                        Tư vấn, bảo hành, chỉ đường
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                      className="text-[#eab308] mt-0.5 shrink-0"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">
                        KHU VỰC
                      </div>
                      <div className="text-[13px] font-medium text-white/90">TP. Hồ Chí Minh</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Danh sách showroom */}
              <div className="bg-white rounded-none p-5 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex-grow">
                <div className="flex items-center gap-2 mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    className="text-[#eab308]"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  <h3 className="text-[13px] font-bold text-gray-900">Danh Sách Showroom</h3>
                </div>
                <div className="space-y-2">
                  {safeShowrooms.map((showroom, idx) => {
                    const isMain = idx === 0;
                    return (
                      <div
                        key={idx}
                        className="bg-gray-50 rounded-none p-3 flex items-start gap-3 border border-transparent hover:border-gray-200 transition-colors"
                      >
                        <div
                          className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${isMain ? 'bg-[#eab308] shadow-[0_0_6px_rgba(234,179,8,0.6)]' : 'bg-gray-300'}`}
                        ></div>
                        <div>
                          <div className="text-[13px] font-bold text-gray-900">{showroom.name}</div>
                          <div className="text-[11px] text-gray-500 mt-0.5">
                            {isMain ? 'Showroom chính' : 'Chi nhánh'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
